import { useEffect, useState } from 'react'
import { Brain, ExternalLink, Plus, Trash2, X } from 'lucide-react'
import type { UserMemory } from '@/types'
import { memoryApi, type MemoryNode } from '@/services/memoryService'

interface UserMemoryPanelProps {
  memory: UserMemory[]
  onAdd: (key: string, value: string) => void
  onRemove: (id: string) => void
  onClear: () => void
  onOpenLibraryMemory?: () => void
}

const NODE_TYPE_COLORS: Record<string, string> = {
  person: '#7c6af7',
  place: '#4fa3e0',
  concept: '#e0a84f',
  preference: '#e06b6b',
  fact: '#4fbe8c',
  skill: '#b06be0',
  project: '#e07a4f',
  event: '#4fc8e0',
  other: '#888',
}

export function UserMemoryPanel({ memory, onAdd, onRemove, onClear, onOpenLibraryMemory }: UserMemoryPanelProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [newKey, setNewKey] = useState('')
  const [newValue, setNewValue] = useState('')
  const [memoryNodes, setMemoryNodes] = useState<MemoryNode[]>([])
  const [nodesLoading, setNodesLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setNodesLoading(true)
      try {
        const result = await memoryApi.listNodes({ limit: 8 })
        if (!cancelled) setMemoryNodes(result.nodes)
      } catch {
        if (!cancelled) setMemoryNodes([])
      } finally {
        if (!cancelled) setNodesLoading(false)
      }
    }
    void load()
    return () => { cancelled = true }
  }, [])

  const handleAdd = () => {
    if (newKey.trim() && newValue.trim()) {
      onAdd(newKey.trim(), newValue.trim())
      setNewKey('')
      setNewValue('')
      setShowAddForm(false)
    }
  }

  return (
    <div className="memory-panel">
      {/* ── Memory Nodes (compact view) ───────────────────────────────── */}
      <div className="memory-panel-section">
        <div className="memory-panel-section-header">
          <div className="memory-panel-title">
            <Brain size={14} />
            <span>Minnenotater</span>
          </div>
          {onOpenLibraryMemory && (
            <button className="memory-panel-open-library" onClick={onOpenLibraryMemory} title="Åpne i Bibliotek">
              <ExternalLink size={12} />
              <span>Se alle</span>
            </button>
          )}
        </div>
        <p className="memory-panel-desc">
          Strukturert hukommelse fra samtaler og agentoppgaver.
        </p>

        {nodesLoading ? (
          <div className="memory-nodes-loading">
            <div className="memory-spinner-sm" />
          </div>
        ) : memoryNodes.length === 0 ? (
          <div className="memory-nodes-empty">
            <p>Ingen minnenotater ennå. Minner bygges opp automatisk fra samtaler.</p>
          </div>
        ) : (
          <div className="memory-nodes-compact-list">
            {memoryNodes.map(node => (
              <div key={node.id} className="memory-node-compact-row">
                <div
                  className="memory-node-compact-dot"
                  style={{ background: NODE_TYPE_COLORS[node.kind] ?? '#888' }}
                />
                <div className="memory-node-compact-body">
                  <span className="memory-node-compact-title">{node.title}</span>
                  <span className="memory-node-compact-preview">
                    {node.content_markdown.slice(0, 60)}{node.content_markdown.length > 60 ? '…' : ''}
                  </span>
                </div>
              </div>
            ))}
            {onOpenLibraryMemory && (
              <button className="memory-nodes-see-all" onClick={onOpenLibraryMemory}>
                Se alle minnenotater →
              </button>
            )}
          </div>
        )}
      </div>

      {/* ── Legacy key-value memory ───────────────────────────────────── */}
      <div className="memory-panel-section memory-panel-section-legacy">
        <div className="memory-panel-section-header">
          <div className="memory-panel-title">
            <span>Nøkkel-verdi-minne</span>
          </div>
        </div>
        <p className="memory-panel-desc">
          Enkel informasjon Sine husker på tvers av alle samtaler.
        </p>

        {memory.length === 0 ? (
          <div className="memory-empty">
            <p>Ingen lagret informasjon ennå.</p>
            <p className="memory-empty-hint">
              Fortell Sine om deg selv — f.eks. "Jeg studerer informatikk ved NTNU" — og den vil huske det.
            </p>
          </div>
        ) : (
          <div className="memory-list">
            {memory.map(item => (
              <div key={item.id} className="memory-item">
                <div className="memory-item-content">
                  <span className="memory-item-key">{item.key}</span>
                  <span className="memory-item-value">{item.value}</span>
                </div>
                <button
                  className="memory-item-remove"
                  onClick={() => onRemove(item.id)}
                  title="Fjern"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {showAddForm && (
          <div className="memory-add-form">
            <input
              className="memory-input"
              placeholder="Nøkkel (f.eks. studiested)"
              value={newKey}
              onChange={e => setNewKey(e.target.value)}
            />
            <input
              className="memory-input"
              placeholder="Verdi (f.eks. NTNU)"
              value={newValue}
              onChange={e => setNewValue(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()}
            />
            <div className="memory-add-actions">
              <button className="memory-btn-cancel" onClick={() => setShowAddForm(false)}>Avbryt</button>
              <button className="memory-btn-save" onClick={handleAdd}>Lagre</button>
            </div>
          </div>
        )}

        <div className="memory-actions">
          <button className="memory-btn-add" onClick={() => setShowAddForm(true)}>
            <Plus size={12} />
            <span>Legg til</span>
          </button>
          {memory.length > 0 && (
            <button className="memory-btn-clear" onClick={onClear}>
              <Trash2 size={12} />
              <span>Slett alt</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
